import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useConfig } from "../../contexts/config";
import { useAuth } from "../../contexts/AuthContext";
import {
  requestAutoLogin,
  exchangeHandoffForToken,
} from "../../services/moodle/userauth";

/**
 * SSO entry point for the participant interface.
 *
 * Production flow:
 * - WordPress/middleware redirects to /sso/callback?key=XXXX
 * - We redirect to Moodle auth/userkey_tipddy/login.php with wantsurl=/sso/complete
 * - Moodle logs the user in and redirects to /sso/complete?handoff=XXXX
 * - We exchange the handoff for a REST token via the plugin endpoint.
 *
 * Demo mode (manual token):
 * - /sso/callback?username=XXXX&token=YYYY
 *
 * TODO: Remove demo mode before production.
 */
export default function Sso() {
  const location = useLocation();
  const navigate = useNavigate();
  const { config } = useConfig();
  const auth = useAuth();
  const [status] = useState("Ingresando al aula...");
  const [error, setError] = useState(null);
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const params = new URLSearchParams(location.search);
    const key = params.get("key");
    const username = params.get("username");
    const token = params.get("token");
    const handoff = params.get("handoff");
    // Emitidos por WordPress junto con `key` (ver get_aula_login_url() en
    // sso.php) para que MiCuenta pueda pedirle datos a su propia API.
    const apiToken = params.get("api_token");
    const wpUrl = params.get("wp_url");

    // Sacar los parámetros sensibles (api_token, key, token, handoff) de la
    // barra de direcciones/historial ANTES de procesar nada — si más abajo
    // se corta con un error, no queda ninguna rama que se olvide de limpiar.
    // Ya están capturados en las variables de arriba, no hace falta la URL.
    if (params.toString()) {
      window.history.replaceState({}, "", location.pathname);
    }

    if (location.pathname === "/sso/callback") {
      handleCallback({ key, username, token, apiToken, wpUrl });
    } else if (location.pathname === "/sso/complete") {
      handleComplete({ username, token, handoff, apiToken, wpUrl });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCallback = async ({ key, username, token, apiToken, wpUrl }) => {
    if (!config?.baseURL) {
      setError("Configuración del aula no disponible.");
      return;
    }

    // Direct token bootstrap (no userkey round-trip). Demo only.
    if (token && username && !key) {
      try {
        await auth.bootstrapSession({ token, username, apiToken, wpUrl });
        window.history.replaceState({}, "", "/listcourses");
        navigate("/listcourses", { replace: true });
        return;
      } catch (e) {
        setError("El token manual no es válido para este usuario.");
        return;
      }
    }

    let loginKey = key;

    if (!loginKey && username) {
      // TODO: DEMO ONLY — remove this branch when WordPress/middleware generates the key.
      if (!config?.token) {
        setError("Token de servicio no configurado.");
        return;
      }
      try {
        const result = await requestAutoLogin({
          // DEMO: el parámetro ?username se usa como email para generar la key.
          email: username,
          baseUrl: config.baseURL,
          tipddytoken: config.token,
        });
        loginKey = result.key;
      } catch (e) {
        setError("No se pudo generar el acceso temporal.");
        return;
      }
    }

    if (!loginKey) {
      setError("Faltan parámetros: se requiere ?key, ?username o ?token.");
      return;
    }

    // api_token/wp_url viajan pegados a wantsurl porque login.php de Moodle
    // redirige de vuelta a esa URL tal cual (no reenvía query params propios).
    const completeParams = new URLSearchParams();
    if (apiToken) completeParams.set("api_token", apiToken);
    if (wpUrl) completeParams.set("wp_url", wpUrl);
    const completeQuery = completeParams.toString();
    const completeUrl = `${window.location.origin}/sso/complete${completeQuery ? `?${completeQuery}` : ""}`;

    const redirectUrl = `${config.baseURL}/auth/userkey_tipddy/login.php?key=${loginKey}&wantsurl=${encodeURIComponent(
      completeUrl
    )}`;

    window.location.replace(redirectUrl);
  };

  const handleComplete = async ({ username, token, handoff, apiToken, wpUrl }) => {
    if (!config?.baseURL) {
      setError("Configuración del aula no disponible.");
      return;
    }

    let loginToken = token;
    let loginUsername = username;

    // Production path: exchange the handoff for a REST token.
    if (handoff) {
      try {
        const tokenData = await exchangeHandoffForToken({
          baseUrl: config.baseURL,
          handoff,
        });
        loginToken = tokenData.token;
        loginUsername = tokenData.username;
      } catch (e) {
        setError("No se pudo intercambiar el handoff por un token de sesión.");
        return;
      }
    }

    if (!loginToken || !loginUsername) {
      setError(
        "Faltan parámetros de sesión. Para el demo, agregue ?token=TOKEN_MANUAL&username=USUARIO."
      );
      return;
    }

    try {
      await auth.bootstrapSession({
        token: loginToken,
        username: loginUsername,
        apiToken,
        wpUrl,
      });

      window.history.replaceState({}, "", "/listcourses");
      navigate("/listcourses", { replace: true });
    } catch (e) {
      setError("No se pudo completar el inicio de sesión.");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#11121f] text-[#e2e1f4] p-6">
      <div className="text-center max-w-md">
        {error ? (
          <>
            <h1 className="text-2xl font-bold mb-4">Acceso al campus</h1>
            <p className="text-red-400">{error}</p>
          </>
        ) : (
          <div className="flex flex-col items-center gap-6">
            <div className="w-10 h-10 border-4 border-[#312559] border-t-transparent rounded-full animate-spin"></div>
            <p className="text-slate-400">{status}</p>
          </div>
        )}
      </div>
    </div>
  );
}
